import {Barcode, DeviceType, Platform} from "@variocube/app-api";
import {
    Badge,
    Checkbox,
    classNames,
    Dialog, DialogContent, DialogFooter, DialogTitle, IconNames, Keyboard,
    layout, MaterialIcon,
    NumberStepInput,
    PushButton, TextInput,
    theme,
    typography,
    useAsync, useBarcodeListener,
    useDevices, usePlatform, useStorageItemValue,
    useUtilization
} from "@variocube/cube-ui";
import {DateTime} from "luxon";
import * as React from "react";
import {Fragment, useCallback, useEffect, useMemo, useState} from "react";
import {style, stylesheet} from "typestyle";
import {
    Accessibility,
    Availability,
    BookingPlan,
    BookingUnit,
    Cart,
    computeAmount, CubeTexts,
    Discount,
    PricingMode
} from "./booking";
import {InfoBox} from "./InfoBox";
import {
    calculateBookingEnd,
    formatCurrency,
    getVolume
} from "./utils";
import {WizardButtonBar} from "./WizardButtonBar";
import {logger} from "./logger";
import {useLocalization} from "../../i18n";
import Link from "@mui/material/Link/Link";
import QRCode from "qrcode";
import {useFlag} from "@variocube/app-ui";
import {useSendVerificationRequest} from "./verify";

interface CartStepProps {
    platform: Platform;
    plans: BookingPlan[];
    availability: Availability;
    maxAmount: number;
    onCart: (cart: Cart) => Promise<any>;
    onCancel: () => void;
    accessibleFrom?: string;
    accessibleUntil?: string;
    accessibility: Accessibility;
    termsUrl?: string;
    privacyPolicyUrl?: string;
    currency: string;
    bookingUnit: BookingUnit;
    pricingMode: PricingMode;
    discounts: Record<string, Discount>;
}

const BoxTypeSortOrder = ["XS", "S", "M", "L", "XL", "XXL"];

const log = logger.sub("CartStep");

function planCompare(a: BookingPlan, b: BookingPlan) {
    return BoxTypeSortOrder.indexOf(a.boxType) - BoxTypeSortOrder.indexOf(b.boxType);
}

export function CartStep(props: CartStepProps) {
    const {
        termsUrl,
        privacyPolicyUrl,
        plans: unsortedPlans,
        onCart,
        onCancel,
        maxAmount,
        availability,
        accessibleFrom,
        accessibleUntil,
        accessibility,
        currency,
        bookingUnit,
        pricingMode,
        discounts,
    } = props;

    const plans = useMemo(() => unsortedPlans.sort(planCompare), [unsortedPlans]);

    const {t, language} = useLocalization();
    const utilization = useUtilization();

    const [units, setUnits] = useState(1);
    const [selectedPlan, setSelectedPlan] = useState<BookingPlan>();
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [privacyPolicyAccepted, setPrivacyPolicyAccepted] = useState(false);
    const [discount, setDiscount] = useState<Discount>();

    const devices = useDevices();
    const hasPaymentTerminal = useMemo(() => Boolean(devices.find(device => device.types.indexOf(DeviceType.PaymentTerminal) >= 0)), [devices]);

    const bookingStart = DateTime.local().setLocale(language);

    const bookingEnd = useMemo(() => {
        return calculateBookingEnd(bookingUnit, bookingStart, units, accessibility, accessibleFrom, accessibleUntil); 
    }, [bookingUnit, bookingStart, units, accessibility]);

    const {
        finalAmount,
        amount
    } = useMemo(() => {
        return (selectedPlan)
            ? computeAmount(pricingMode, selectedPlan, units, discount)
            : {} as any;
    }, [selectedPlan, pricingMode, units, discount]);

    useEffect(() => {
        if (pricingMode == "Package") {
            if (selectedPlan) {
                if (!selectedPlan.packages.find(packageLine => packageLine.quantity == units)) {
                    const first = selectedPlan.packages[0];
                    if (first) {
                        setUnits(first.quantity);
                    }
                }
            }
            else {
                const availableQuantities = new Set(plans.flatMap(
                    plan => plan.packages.map(packageLine => packageLine.quantity)
                ));
                if (!availableQuantities.has(units)) {
                    const lowestQuantity = [...availableQuantities].sort((a, b) => a - b).shift();
                    if (lowestQuantity) {
                        setUnits(lowestQuantity);
                    }
                }
            }
        }
    }, [selectedPlan, pricingMode, units]);

    const cubeTexts = useStorageItemValue<CubeTexts>("cubeTexts");
    const [cubeText, setCubeText] = useState<string>();
    const [autoEnableDiscount, setEnableDiscount] = useFlag(false);

    useEffect(() => {
        if (cubeTexts && cubeText) {
            const localText = cubeTexts.discounts[language];
            if (localText) setCubeText(localText);
        }
    }, [cubeTexts, cubeText, language]);

    const isAvailable = useCallback((boxType: string) => {
        return units <= (availability[boxType]?.max ?? 0) && (utilization.types[boxType]?.available ?? 0) > 0;
    }, [availability, units, utilization]);

    const maxUnits = Object.values(availability)
        .reduce((prev, curr) => Math.max(prev, curr.max), 0);

    const error = useMemo(() => {
        if (!selectedPlan) {
            return t("SelectLockerSize");
        }
        else if (selectedPlan && !isAvailable(selectedPlan.boxType)) {
            return t("MaxDaysExceeded");
        }
        else if (!!finalAmount && (finalAmount > maxAmount)) {
            return t("MaxAmountExceeded", {maxAmount: formatCurrency(maxAmount, currency, language)});
        }
        else if (termsUrl && !termsAccepted) {
            return t("termsNotAccepted");
        }
        else if (privacyPolicyUrl && !privacyPolicyAccepted) {
            return t("privacyPolicyNotAccepted");
        }
    }, [selectedPlan, finalAmount, maxAmount, isAvailable, termsAccepted, privacyPolicyAccepted]);

    const submitNext = useCallback(async () => {
        if (selectedPlan && typeof finalAmount != "undefined") {
            const {boxType} = selectedPlan;
            try {
                await onCart({
                    boxType,
                    units: units,
                    amount: finalAmount,
                    from: bookingStart,
                    until: bookingEnd,
                    discount
                });
            }
            catch (error) {
                log.error("Error in onCart handler", error);
            }
        }
    }, [selectedPlan, units, onCart, bookingStart, bookingEnd, finalAmount, discount]);

    const handleNext = useCallback(async () => {
        if (cubeTexts && Object.keys(cubeTexts.discounts).length > 0 && !discount) {
            setCubeText(cubeTexts.discounts[language] ?? Object.values(cubeTexts.discounts).shift());
            return;
        }
        await submitNext();
    }, [cubeTexts, language, discount, submitNext]);



    const handleBack = useCallback(() => {
        setCubeText(undefined);
        setEnableDiscount();
    }, [])

    const quantityOptions = useMemo(() => {
        if (pricingMode == "Package") {
            if (selectedPlan) {
                return selectedPlan.packages.map(packageLine => packageLine.quantity).sort((a, b) => a - b);
            }
            else {
                const availableQuantities = new Set(plans.flatMap(
                    plan => plan.packages.map(packageLine => packageLine.quantity)
                ));
                return [...availableQuantities].sort((a, b) => a - b);
            }
        }
    }, [pricingMode, selectedPlan]);

    function getPlanButtonSize() {
        switch (plans.length) {
            case 0:
            case 1:
            case 2:
            case 3:
                return "large";

            case 4:
                return "medium";

            case 5:
            case 6:
            default:
                return "small";
        }
    }

    const planButtonSize = getPlanButtonSize();

    return (
        <div className={classNames(layout.fill, layout.vertical, layout.spaceAround, styles.view)}>
            {!cubeText && (
                <Fragment>
                    <div className={layout.horizontal}>
                        <div className={classNames(layout.horizontal, styles.planButtonContainer)}>
                            {plans.map((plan) => (
                                <PlanRadioButton
                                    key={plan.boxType}
                                    plan={plan}
                                    currency={currency}
                                    size={planButtonSize}
                                    icon={getIcon(plan)}
                                    fullyBooked={!isAvailable(plan.boxType)}
                                    paymentTerminal={hasPaymentTerminal}
                                    checked={!!selectedPlan && selectedPlan.boxType == plan.boxType}
                                    onCheck={setSelectedPlan}
                                    bookingUnit={bookingUnit}
                                    pricingMode={pricingMode}
                                />
                            ))}
                        </div>

                        <div className={classNames(styles.rightContainer)}>
                            <div className={classNames(styles.bookingPeriod)}>
                                {(bookingUnit != "Days" || maxUnits > 1) && (
                                    <Fragment>
                                        <div className={styles.bookingPeriodTitle}>
                                            {t(bookingUnit == "Days" ? "PeriodInDays" : "PeriodInHours")}
                                        </div>
                                        <NumberStepInput
                                            value={units}
                                            onChange={setUnits}
                                            min={1}
                                            className={style({margin: "0.75em 0"})}
                                            options={quantityOptions}
                                        />
                                        {bookingUnit == "Days" && (
                                            <Fragment>
                                                <div>
                                                    <strong>
                                                        {bookingStart.toLocaleString(DateTime.DATE_SHORT)} - {bookingEnd.toLocaleString(DateTime.DATE_SHORT)}
                                                    </strong>
                                                </div>
                                                <div>
                                                    {t("BookingPeriodInfo", {accessibleUntil: bookingEnd.toLocaleString(DateTime.TIME_SIMPLE)})}
                                                </div>
                                            </Fragment>
                                        )}
                                        {bookingUnit == "Hours" && (
                                            <Fragment>
                                                <div>{t("BookingPeriodInfo", {accessibleUntil: ""})}</div>
                                                <div>
                                                    <strong dangerouslySetInnerHTML={{ // because the english string containers html entities
                                                        __html: bookingEnd.toLocaleString(DateTime.DATETIME_SHORT)
                                                    }}/>
                                                </div>
                                            </Fragment>
                                        )}
                                    </Fragment>
                                )}
                                {bookingUnit == "Days" && maxUnits <= 1 && (
                                    <Fragment>
                                        <div className={styles.bookingPeriodTitle}>
                                            {t("BookingFor")}
                                        </div>
                                        <div className={styles.bookingPeriodToday}>
                                            {t("Today")}
                                        </div>
                                        <div>
                                            <strong>
                                                {bookingStart.toLocaleString(DateTime.DATE_SHORT)}
                                            </strong>
                                        </div>
                                        <div>
                                            {t("BookingPeriodInfo", {accessibleUntil: bookingEnd.toLocaleString(DateTime.TIME_SIMPLE)})}
                                        </div>
                                    </Fragment>
                                )}
                            </div>
                            <div className={classNames(styles.terms)}>
                                {(!termsUrl && !privacyPolicyUrl) &&
                                    <InfoBox>
                                        {t("BookingFlowInfo")}
                                    </InfoBox>
                                }

                                {termsUrl && (
                                    <TermsCheckbox
                                        lead={t("termsLead")}
                                        linkText={t("terms")}
                                        linkUrl={termsUrl}
                                        checked={termsAccepted}
                                        onChange={setTermsAccepted}
                                        dialogTitle={t("termsQrCodeDialogHeading")}
                                        closeLabel={t("Close")}
                                    />
                                )}
                                {privacyPolicyUrl && (
                                    <TermsCheckbox
                                        lead={t("privacyPolicyLead")}
                                        linkText={t("privacyPolicy")}
                                        linkUrl={privacyPolicyUrl}
                                        checked={privacyPolicyAccepted}
                                        onChange={setPrivacyPolicyAccepted}
                                        dialogTitle={t("privacyPolicyQrCodeDialogHeading")}
                                        closeLabel={t("Close")}
                                    />
                                )}
                            </div>
                        </div>
                    </div>

                    <WizardButtonBar
                        className={styles.buttonArea}
                        onCancel={onCancel}
                        onNext={handleNext}
                        enableNext={!error}
                        info={
                            <div style={{marginRight: "1em"}}>
                                {!error && (
                                    <div style={{display: 'flex', alignItems: 'center'}}>
                                        <div>
                                            <span className={styles.amountLabel}>{t("Amount")}</span>
                                            {" "}
                                            {amount != undefined && finalAmount != undefined && (
                                                <span
                                                    style={{marginRight: "1em"}}
                                                    className={styles.amountValue}
                                                >
                                                    {discount && (
                                                        <s>{formatCurrency(amount, currency, language)}</s>
                                                    )}
                                                    {" "}
                                                    {formatCurrency(finalAmount, currency, language)}
                                                </span>
                                            )}
                                        </div>
                                        <DiscountButton
                                            discounts={discounts}
                                            onSubmit={setDiscount}
                                            autoEnable={autoEnableDiscount}
                                        />
                                    </div>
                                )}
                                {error && <div>{error}</div>}
                            </div>
                        }
                    />
                </Fragment>
            )}
            {cubeText && (
                <Fragment>
                    <div dangerouslySetInnerHTML={{__html: cubeText}}/>
                    <WizardButtonBar
                        className={styles.buttonArea}
                        cancelTitle={t('Back')}
                        onCancel={handleBack}
                        nextTitle={t('Skip')}
                        onNext={submitNext}
                        enableNext
                    />
                </Fragment>
            )}
        </div>
    );
}

interface DiscountButtonProps {
    discounts: Record<string, Discount>;
    onSubmit: (discount: Discount) => void;
    autoEnable: boolean;
}

function DiscountButton({discounts, onSubmit, autoEnable}: DiscountButtonProps) {
    if (Object.keys(discounts).length > 0) {
        return (
            <DiscountViaCode
                discounts={discounts}
                onSubmit={onSubmit}
            />
        )
    }

    const westfieldVerificationUrl = useStorageItemValue<string>("westfieldVerificationUrl");
    if (westfieldVerificationUrl) {
        return (
            <DiscountViaWestfieldLoyaltyService
                onSubmit={onSubmit}
                autoEnable={autoEnable}
            />
        )
    }

    return <div/>
}

interface DiscountViaCodeProps {
    discounts: Record<string, Discount>;
    onSubmit: (discount: Discount) => void;
}

function DiscountViaCode({discounts, onSubmit}: DiscountViaCodeProps) {
    const {t} = useLocalization();

    const [open, setOpen, clearOpen] = useFlag(false);
    const [error, setError, clearError] = useFlag(false);
    const [code, setCode] = useState('');

    const handleType = useCallback((key: string) => setCode(prev => prev + key), []);

    const handleSubmit = useCallback(() => {
        clearError();
        const discount = discounts[code];
        if (!discount) {
            setError();
        } else {
            onSubmit(discount);
            clearOpen();
        }
        setCode('');
    }, [code, discounts]);

    const handleClose = useCallback(() => {
        clearError();
        clearOpen();
        setCode('');
    }, [])

    return (
        <Fragment>
            <PushButton secondary onClick={setOpen}>
                {t('DiscountCodeButtonLabel')}
            </PushButton>
            <Dialog open={open}>
                <DialogTitle>{t('DiscountCodeTitle')}</DialogTitle>
                <DialogContent>
                    <TextInput
                        placeholder="Code"
                        value={code}
                        onChange={setCode}
                    />
                    {error && (
                        <div style={{marginBottom: '0.5rem'}} className={classNames(layout.horizontal, layout.center)}>
                            <span className={classNames(styles.errorWrapper)}>
                                <strong>{t('InvalidDiscountCode')}</strong>
                            </span>
                        </div>
                    )}
                    <Keyboard onType={handleType}/>
                </DialogContent>
                <DialogFooter>
                    <div className={classNames(layout.horizontal, layout.spaceBetween)}>
                        <PushButton onClick={handleClose}>{t('Close')}</PushButton>
                        <PushButton primary onClick={handleSubmit}>{t('Submit')}</PushButton>
                    </div>
                </DialogFooter>
            </Dialog>
        </Fragment>
    )

}

interface DiscountViaWestfieldLoyaltyServiceProps {
    onSubmit: (discount: Discount) => void;
    autoEnable: boolean;
}

function DiscountViaWestfieldLoyaltyService({onSubmit, autoEnable}: DiscountViaWestfieldLoyaltyServiceProps) {
    const {t} = useLocalization();

    const [open, setOpen, clearOpen] = useFlag(autoEnable);
    const [success, setSuccess, clearSuccess] = useFlag(false);

    const platform = usePlatform();
    const send = useSendVerificationRequest(platform, "westfieldVerificationUrl", handleVerification);

    useEffect(() => {
        if (send.invalid) {
            setOpen();
        }
    }, [send.invalid]);

    function handleVerification({code, amount}: { code?: string, amount?: number }) {
        if (amount) {
            onSubmit({code, value: amount, type: 'WestfieldLoyaltyService'});
            setSuccess();
        }
        setTimeout(handleClose, 2500);
    }

    useBarcodeListener(async (code: Barcode) => {
        await send.submit({}, {code: atob(code.code)})
    })

    const handleClose = useCallback(() => {
        send.reset();
        clearSuccess();
        clearOpen();
    }, [])

    return (
        <Fragment>
            <PushButton secondary onClick={setOpen}>
                {t('DiscountLoyaltyButtonLabel')}
            </PushButton>

            <Dialog open={(open || success) && !send.loading}
                    class={success ? classNames(styles.successWrapper) : undefined}>
                <DialogContent>
                    <div className={classNames(layout.horizontal, layout.spaceBetween, layout.alignCenter)}>
                        {send.invalid && (
                            <Fragment>
                                <MaterialIcon icon={IconNames.error} className={classNames(styles.discountInfoIcon, styles.error)}/>
                                <span>{t('DiscountLoyaltyError')}</span>
                            </Fragment>
                        )}
                        {!send.invalid && !success && (
                            <Fragment>
                                <MaterialIcon icon={IconNames.info} className={classNames(styles.discountInfoIcon)}/>
                                <div className={classNames(layout.vertical)}>
                                    <span
                                        className={classNames(typography.title)}>{t('DiscountLoyaltyButtonLabel')}</span>
                                    <span>{t('DiscountLoyaltyMessage')}</span>
                                </div>
                            </Fragment>
                        )}
                        {success && (
                            <Fragment>
                                <MaterialIcon icon={IconNames.check} className={classNames(styles.discountInfoIcon)}/>
                                <div className={classNames(layout.vertical)}>
                                    <span>{t('DiscountLoyaltySuccessMessage')}</span>
                                </div>
                            </Fragment>
                        )}
                    </div>
                </DialogContent>
                <DialogFooter>
                    <div className={classNames(layout.center)}>
                        <PushButton onClick={handleClose}>{t('Close')}</PushButton>
                    </div>
                </DialogFooter>
            </Dialog>
            {send.loading && (
                <Dialog open={true}>
                    <DialogContent>
                        {t('PleaseWait')}
                    </DialogContent>
                </Dialog>
            )}
        </Fragment>
    )
}

interface TermsCheckboxProps {
    lead: string;
    linkText: string;
    linkUrl: string;
    checked: boolean;
    onChange: (checked: boolean) => any;
    dialogTitle: string;
    closeLabel: string;
}

function TermsCheckbox(props: TermsCheckboxProps) {
    const {lead, linkText, linkUrl, checked, onChange, dialogTitle, closeLabel} = props;

    const [dialogOpen, setDialogOpen, clearDialogOpen] = useFlag(false);

    const {result: dataUrl} = useAsync(() => QRCode.toDataURL(linkUrl, {type: 'image/png', width: 400}), [linkUrl]);

    return (
        <div style={{marginBottom: "1em"}}>
            <Checkbox
                checked={checked}
                onChange={onChange}
                color="choice"
                size="large"
            >
                {lead} <Link className={styles.termsLink} onClick={setDialogOpen}>{linkText}</Link>
            </Checkbox>
            <Dialog open={dialogOpen}>
                <div className={classNames(layout.vertical, typography.alignCenter)}>
                    <div className={classNames(theme.moreContrast, layout.paddingMedium)}>
                        <p><strong>{dialogTitle}</strong></p>
                        <img className={styles.qrCode}
                             src={dataUrl}
                             alt={dialogTitle}
                        />
                        <div>
                            <PushButton primary onClick={clearDialogOpen}>
                                {closeLabel}
                            </PushButton>
                        </div>
                    </div>
                </div>
            </Dialog>
        </div>
    )
}

interface PlanRadioButtonProps {
    plan: BookingPlan;
    currency: string;
    checked: boolean;
    fullyBooked: boolean;
    paymentTerminal: boolean;
    onCheck: (plan: BookingPlan) => void;
    icon: React.ReactNode;
    size: "small" | "medium" | "large";
    bookingUnit: BookingUnit;
    pricingMode: PricingMode;
}

function PlanRadioButton(props: PlanRadioButtonProps) {
    const {
        plan,
        currency,
        onCheck,
        checked,
        icon,
        fullyBooked,
        paymentTerminal,
        size,
        bookingUnit,
        pricingMode,
    } = props;

    const {t, language} = useLocalization();

    const pricePerDay = bookingUnit == "Days" && pricingMode == "Unit" && plan.units.find(line => line.from == 0)?.price;

    const isFree = pricingMode == "Unit"
        ? plan.units.every(line => line.price == 0)
        : plan.packages.every(pkg => pkg.price == 0);

    const error = useMemo(() => {
        if (fullyBooked) {
            return t("FullyBooked");
        } else if (!isFree && !paymentTerminal) {
            return t("PaymentSystemUnavailable");
        }
    }, [fullyBooked, plan, paymentTerminal, isFree]);

    const handleClick = useCallback(() => !error && onCheck(plan), [error, onCheck, plan]);


    return (
        <div className={styles.planRadioButton} onClick={handleClick}>
            <div
                style={{
                    fontSize: size == "small" ? "0.75em" : "1em",
                    width: size == "medium" ? "8em" : "11em",
                    height: size != "small" ? "15em" : "12em"
                }}
                className={classNames(
                    layout.vertical,
                    layout.end,
                    styles.planRadioButtonInner,
                    checked && styles.planRadioButtonChecked,
                    error && styles.planRadioButtonDisabled
                )}>
                <div className={styles.planBoxType}>{plan.boxType}</div>
                <div>{icon}</div>
                {typeof pricePerDay == "number" && (
                    <div className={styles.planPrice}>
                        {formatCurrency(pricePerDay, currency, language)} {t("PerDay")}
                    </div>
                )}
                {pricingMode == "Package" && (
                    <div style={{paddingBottom: "0.25em"}}>
                        {plan.packages
                            .sort((p1, p2) => p1.quantity - p2.quantity)
                            .map(({quantity, price}) =>
                            <div className={styles.planPricePackage}>
                                {t(`PackagePrice${bookingUnit}`, {
                                    quantity,
                                    price: formatCurrency(price, currency, language)
                                })}
                            </div>
                        )}
                    </div>
                )}
                {error && (
                    <Badge error className={styles.errorBadge}>
                        {error}
                    </Badge>
                )}
            </div>
        </div>
    );
}

function getIcon(plan: BookingPlan) {
    if (plan.boxType.toLowerCase().includes("charge")) {
        return <ChargerIcon/>;
    } else if (plan.boxType.toLowerCase().includes("ski")) {
        return <SkiIcon/>;
    }
    const volume = getVolume(plan.dimensions);
    if (volume) {
        // Variocube standard box dimensions are:
        // L: 460 x 730 x 950 = 319010000
        // M: 460 x 580 x 950 = 253460000
        // S: 460 x 430 x 950 = 187910000
        if (volume < 50_000_000) {
            return <ExtraSmallIcon/>;
        }
        if (volume < 200_000_000) {
            return <SmallIcon/>;
        }
        if (volume > 300_000_000) {
            return <LargeIcon/>;
        }
        return <MediumIcon/>;
    }
    switch (plan.boxType) {
        case "XS":
            return <ExtraSmallIcon/>;
        case "S":
            return <SmallIcon/>;
        case "M":
            return <MediumIcon/>;
        case "L":
            return <LargeIcon/>;
        default:
            return null;
    }
}

const SmallIcon = ({width = "32%"}: { width?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36.81 52.19" style={{width}} className={styles.planIcon}>
        <g>
            <path
                d="M32.37,4.49H4.44A4.44,4.44,0,0,0,0,8.93v7.66A4.44,4.44,0,0,0,4.44,21H7.79V17.14a1.24,1.24,0,1,1,2.47,0V21H26.55v-3.9a1.24,1.24,0,1,1,2.47,0h0V21h3.35a4.45,4.45,0,0,0,4.44-4.44V8.93A4.44,4.44,0,0,0,32.37,4.49Z"/>
            <rect x="6.28" y="35.55" width="24.25" height="10.27"/>
            <path
                d="M29,23.51V25.9a1.24,1.24,0,0,1-2.48,0h0V23.51H10.26V25.9a1.24,1.24,0,1,1-2.47,0V23.51H4.44a6.86,6.86,0,0,1-3.73-1.1v27a2.82,2.82,0,0,0,2.82,2.82H33.29a2.82,2.82,0,0,0,2.82-2.82v-27a6.86,6.86,0,0,1-3.73,1.1Zm4,10.8V47.05a1.24,1.24,0,0,1-1.24,1.24H5a1.23,1.23,0,0,1-1.23-1.24V34.31A1.24,1.24,0,0,1,5,33.07H31.77A1.25,1.25,0,0,1,33,34.31Z"/>
            <path d="M27.93,2.92H22.35V2.43A2.43,2.43,0,0,1,24.78,0h6.14a2.42,2.42,0,0,1,2.42,2.43v.49Z"/>
            <path d="M9,2.92H3.41V2.43A2.43,2.43,0,0,1,5.84,0H12A2.43,2.43,0,0,1,14.4,2.43v.49Z"/>
        </g>
    </svg>
);

const ExtraSmallIcon = () => (
    <SmallIcon width="26%"/>
)

const MediumIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 44.11 88.17" style={{width: "36%"}}
         className={styles.planIcon}>
        <g>
            <path
                d="M33.82,88.17a1.48,1.48,0,0,1-1.47-1.47V85.23H11.78V86.7a1.47,1.47,0,1,1-2.94,0h0V85.23H4.43A4.42,4.42,0,0,1,0,80.82S0,70.53,0,61,0,41.17,0,41.14H0c0-.06,3.86-.1,11.37-.1,12.86,0,32.52.1,32.72.1V80.82a4.41,4.41,0,0,1-4.41,4.41H35.29V86.7A1.47,1.47,0,0,1,33.82,88.17ZM22,46.24a1.5,1.5,0,0,0-1.51,1.5V79.21a1.51,1.51,0,0,0,3,0V47.74A1.51,1.51,0,0,0,22,46.24Zm11.7-.09a1.51,1.51,0,0,0-1.51,1.51V79.13a1.51,1.51,0,0,0,3,0V47.66a1.52,1.52,0,0,0-1.51-1.51Zm-23.6,0a1.52,1.52,0,0,0-1.51,1.51V79.13a1.52,1.52,0,0,0,1.55,1.47,1.51,1.51,0,0,0,1.47-1.47V47.66a1.51,1.51,0,0,0-1.51-1.51Z"/>
            <path
                d="M29.41,38.21h14.7V33.8a4.41,4.41,0,0,0-4.4-4.41H36.77a1.47,1.47,0,1,0-2.94,0H30.89V8.55A4.4,4.4,0,0,0,29.41,0H14.71a4.4,4.4,0,0,0-1.46,8.55V29.39H10.31a1.47,1.47,0,0,0-2.94,0H4.43A4.42,4.42,0,0,0,0,33.8v4.41H29.41Zm-1.47-8.82H16.19V8.82H28Z"/>
        </g>
    </svg>
);

const LargeIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80.74 68.88" style={{width: "70%"}}
         className={styles.planIcon}>
        <g>
            <path
                d="M14.33,12.69V68.88H7.45a6.41,6.41,0,0,1-2.54-.58,8.56,8.56,0,0,1-2.4-1.54A9.23,9.23,0,0,1,.72,64.57,4.9,4.9,0,0,1,0,62.07V19.35a4.47,4.47,0,0,1,.72-2.26A9.43,9.43,0,0,1,2.51,15a10.64,10.64,0,0,1,2.33-1.61,5.2,5.2,0,0,1,2.33-.65Zm3,.07H28.58V3.94A2.5,2.5,0,0,1,29,2.69,6.56,6.56,0,0,1,30,1.4a5.66,5.66,0,0,1,1.33-1A2.68,2.68,0,0,1,32.6,0H48.15a3.14,3.14,0,0,1,1.4.4,5,5,0,0,1,1.33,1,6.23,6.23,0,0,1,1,1.36,2.94,2.94,0,0,1,.39,1.4v8.6H63.42V68.87H17.34V12.76ZM32.88,4.3v8.39H48V4.3Zm33.6,8.39h7.31a4.84,4.84,0,0,1,2.29.64,10.22,10.22,0,0,1,4,3.83,4.52,4.52,0,0,1,.69,2.19V62.06a5.08,5.08,0,0,1-.69,2.5,8.28,8.28,0,0,1-1.75,2.19,8.9,8.9,0,0,1-2.37,1.55,6.17,6.17,0,0,1-2.5.57h-7V12.69Z"/>
        </g>
    </svg>
);

const ChargerIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 453.543307 453.543307" style={{width: "70%"}} className={styles.planIcon}>
        <path d="m388.276599,158.538632c-8.830287-20.872574-21.46594-39.616344-37.562204-55.712709-16.092212-16.092212-34.840236-28.731917-55.712709-37.558152-6.548102-2.767759-13.216558-5.124813-19.992909-7.06265l-5.215991,20.067472c24.789784,7.141569,47.504762,20.478381,66.252686,39.226304,29.184261,29.188415,45.259858,67.995604,45.259858,109.271793,0,41.280343-16.075597,80.087532-45.259858,109.271793-29.188415,29.188313-67.995604,45.26391-109.275947,45.26391-10.116708,0-20.08824-.966791-29.806602-2.859039l-12.20823,18.544596c13.652288,3.365381,27.706974,5.062609,42.014832,5.062609,23.661102,0,46.61679-4.635187,68.23216-13.776796,20.872473-8.830389,39.620497-21.46594,55.712709-37.562305,16.096264-16.096365,28.731917-34.840135,37.562204-55.712709,9.14161-21.61527,13.776796-44.570957,13.776796-68.232059,0-23.656948-4.635187-46.612636-13.776796-68.232059Z"/>
        <path d="m117.497834,336.042484c-29.188415-29.184261-45.259858-67.99145-45.259858-109.271793,0-41.276189,16.071443-80.083378,45.259858-109.271793,29.001601-29.001601,67.505877-45.060583,108.487459-45.259757l13.141792-20.320643c-4.095615-.290554-8.216254-.431576-12.35756-.431576-23.656948,0-46.612535,4.639239-68.227905,13.780849-20.876728,8.826235-39.620497,21.46594-55.712709,37.558152-16.096365,16.096365-28.731917,34.840135-37.562305,55.712709-9.14161,21.619423-13.776695,44.575111-13.776695,68.232059,0,23.661102,4.635085,46.61679,13.776695,68.232059,8.830389,20.872574,21.46594,39.616344,37.562305,55.712709,13.780849,13.785002,29.507943,25.034648,46.857398,33.520586l5.979657-20.175366c-13.905357-7.224541-26.769158-16.619221-38.168133-28.018195Z"/>
        <polygon points="229.886801 180.168591 269.014633 29.613113 136.417882 234.726075 208.706451 230.892951 151.466611 423.930194 317.124007 172.300941 229.886801 180.168591"/>
    </svg>
);

const SkiIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 453.543307 453.543307" style={{width: "80%"}}
         className={styles.planIcon}>
        <path
            d="m212.605299,436.417111h-17.396063c-3.837133,0-6.947744-3.110611-6.947744-6.947744,0,0,3.702374-151.821856,3.676514-202.448912-.024813-48.577579-3.676514-194.248499-3.676514-194.248499,0-8.640914,7.004845-15.645759,15.645759-15.645759h.000032c8.640914,0,15.64576,7.004845,15.64576,15.64576,0,0-3.651702,145.67092-3.676515,194.248499-.02586,50.627056,3.676514,202.448912,3.676514,202.448912,0,3.837133-3.110611,6.947744-6.947744,6.947744Z"/>
        <path
            d="m258.334071,436.417111h-17.396063c-3.837133,0-6.947744-3.110611-6.947744-6.947744,0,0,3.702374-151.821856,3.676514-202.448912-.024813-48.577579-3.676514-194.248499-3.676514-194.248499,0-8.640914,7.004845-15.645759,15.645759-15.645759h.000032c8.640914,0,15.64576,7.004845,15.64576,15.64576,0,0-3.651702,145.67092-3.676515,194.248499-.02586,50.627056,3.676514,202.448912,3.676514,202.448912,0,3.837133-3.110611,6.947744-6.947744,6.947744Z"/>
        <g fill="none" stroke="currentColor" strokeWidth={7} strokeLinecap="round">
            <line x1="203.907267" y1="187.562596" x2="203.907267" y2="265.980712"/>
            <line x1="249.63604" y1="187.562596" x2="249.63604" y2="265.980712"/>
            <line x1="278.285909" y1="341.287872" x2="292.50866" y2="341.287872"/>
            <line x1="285.397285" y1="110.624858" x2="285.397285" y2="360.185565"/>
            <rect x="281.983372" y="107.488968" width="6.827826" height="48.553423" rx=".894363"
                  ry=".894363"/>
            <line x1="161.034647" y1="341.287872" x2="175.257398" y2="341.287872"/>
            <line x1="168.146022" y1="110.624858" x2="168.146022" y2="360.185565"/>
            <rect x="164.73211" y="107.488968" width="6.827825" height="48.553423" rx=".894363"
                  ry=".894363"/>
        </g>
    </svg>
);


const styles = stylesheet({
    view: {
        padding: "1em"
    },
    planButtonContainer: {
        flex: 1,
        flexFlow: "row wrap",
    },
    rightContainer: {
        marginLeft: "1em",
        flex: 0,
        minWidth: "18em",
    },
    planRadioButton: {
        margin: "0 0.75em 0.75em 0",
    },
    planRadioButtonInner: {
        width: "11em",
        height: "11em",
        border: "0.25em solid var(--color-choice)",
        fill: "#fff",
        color: "#fff",
        position: "relative",
    },
    planRadioButtonChecked: {
        backgroundColor: "var(--color-choice)",
        fill: "var(--color-background)",
        color: "var(--color-background)",
    },
    planRadioButtonDisabled: {
        opacity: 0.5,
    },
    planBoxType: {
        position: "absolute",
        top: "0.5rem",
        left: "0.75rem",
        fontWeight: "bold",
        fontSize: "1.5em"
    },
    planIcon: {
        display: "block",
        margin: "0 auto 0.5em auto",
    },
    planPrice: {
        fontWeight: "bold",
        textAlign: "center",
        padding: "0.25em"
    },
    planPricePackage: {
        textAlign: "center",
        fontSize: "0.675em"
    },
    errorBadge: {
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translateX(-50%) translateY(-50%)"
    },
    bookingPeriod: {
    },
    bookingPeriodTitle: {
        fontSize: "1.5em"
    },
    bookingPeriodToday: {
        fontWeight: 500,
        fontSize: "2em",
        margin: "0.5rem 0"
    },
    infoIcon: {
        width: "2.5em",
        marginRight: "0.75em"
    },
    amountLabel: {
        fontSize: "2em",
    },
    amountValue: {
        fontSize: "2em",
        fontWeight: "bold",
    },
    buttonArea: {
        marginTop: "1.5em"
    },
    qrCode: {
        padding: "2em"
    },
    terms: {
        marginTop: "1.5em",
    },
    termsLink: {
        color: "var(--color-choice) !important"
    },
    errorWrapper: {
        color: '#fff',
        padding: '0.5em',
        background: 'var(--color-error)'
    },
    discountInfoIcon: {
        fontSize: '3.5rem',
        marginRight: '0.75em',
    },
    error: {
        color: 'var(--color-error)'
    },
    successWrapper: {
        color: '#fff',
        padding: '0.5em',
        background: 'var(--color-success)'
    }
});
